import React, { useEffect } from 'react';
import styles from './Welcome.module.scss';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import useTheme from '../../../hooks/useTheme';

import portalUi from '../../../Assets/Images/portal-ui.png';
import portalUiDark from '../../../Assets/Images/portal-ui-dark.png';
const featuresData = [
    {
        icon: "hub",
        title: "Simplified API Discovery",
        description: "Quickly find the APIs you need with our centralized and searchable directory.",
    },
    {
        icon: "code",
        title: "Improved Developer Experience",
        description: "Clear documentation, code samples, and a straightforward process to get you started.",
    },
    {
        icon: "history",
        title: "Accelerate Integration",
        description: "Reduce the time and effort required to integrate Synnex APIs into your applications.",
    },
];

const howItWorksData = [
    {
        icon: "filter_1",
        title: "Discover APIs",
        description: "Browse our comprehensive API catalog to find the services you need.",
    },
    {
        icon: "filter_2",
        title: "Get Your Keys",
        description: "Authenticate and get your API keys in a few simple steps.",
    },
    {
        icon: "filter_3",
        title: "Start Building",
        description: "Integrate with our APIs and start creating amazing applications.",
    },
];

const featuredAPIsData = [
    {
        title: "Cloud Services API",
        description: "Manage your cloud resources and services programmatically.",
        link: "/api-reference"
    },
    {
        title: "E-commerce API",
        description: "Integrate our product catalog and ordering system into your applications.",
        link: "/api-reference"
    },
    {
        title: "Logistics API",
        description: "Streamline your shipping and tracking processes with our logistics services.",
        link: "/api-reference"
    },
];

const Welcome = () => {
    const navigate = useNavigate();
    const isAuthenticated = useIsAuthenticated();
    const theme = useTheme();

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/guides');
        }
    }, [isAuthenticated, navigate]);

  return (
    <div className={styles.welcomePage}>
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <clipPath id="hero-wave-clip" clipPathUnits="objectBoundingBox">
            <path d="M0,0 H1 V0.85 C0.75,1, 0.25,0.7, 0,0.85 V0" />
          </clipPath>
        </defs>
      </svg>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1 className={`${styles.heroTitle} text-lg`}>The Gateway to Synnex APIs</h1>
          <p className={`${styles.heroSubtitle} text-lmd`}>
            Discover, integrate, and manage Synnex APIs with our unified developer platform.
          </p>
          <div className={styles.heroActions}>
            <button
              className={styles.ctaButton}
              onClick={() => navigate("/guides")}
            >
              Get Started
            </button>
          </div>
        </div>
        <div className={styles.heroImage}>
          <img src={theme === 'theme--dark' ? portalUiDark : portalUi} alt="Synnex End-User Portal UI" />
        </div>
      </section>
      
      <main className={styles.mainContent}>
        {/* Features Section */}
        <section className={styles.featuresSection}>
          <h1 className={styles.sectionTitle} data-aos="fade-up">Why Our Platform?</h1>
          <div className={styles.featuresGrid}>
            {featuresData.map((feature, index) => (
              <div className={styles.featureCard} key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                <i className="material-icons">{feature.icon}</i>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Integrates With Section */}
        <section className={styles.integratesWithSection} data-aos="fade-up">
            <h1 className={styles.sectionTitle}>
                Integrates With
            </h1>
            <div className={styles.integrationLogos}>
                <img src="https://partner.synnex.com.au/wp-content/uploads/2024/02/Microsoft.png" alt="Microsoft" />
            </div>
        </section>

        {/* How It Works Section */}
        <section className={styles.howItWorksSection}>
            <h1 className={styles.sectionTitle} data-aos="fade-up">How It Works</h1>
            <div className={styles.stepsGrid}>
                {howItWorksData.map((step, index) => (
                    <div
                        className={styles.stepCard}
                        key={index}
                        data-aos={(index % 2 === 0) ? "fade-right" : "fade-left"}
                    >
                        <div className={styles.stepContent}>
                            <h3>{step.title}</h3>
                            <p>{step.description}</p>
                        </div>
                        <div className={styles.stepIcon}>
                            <i className="material-icons">{step.icon}</i>
                        </div>
                    </div>
                ))}
            </div>
        </section>
        
      </main>

      <footer className={styles.footer}>
        <p>&copy; {new Date().getFullYear()} Synnex Australia Pty Ltd. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Welcome;